import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2, Shield } from "react-feather";
import Swal from "sweetalert2";
import confirmDelete from "../components/confirmDelete";
import { fetchRoles, createRoleThunk, updateRoleThunk, deleteRoleThunk } from "../../core/redux/slices/userSlice";
import { getRolePermissions, updateRolePermissions } from "../../core/api/userService";

const MODULES = [
  "Patients", "Procedures", "Doctors", "Products", "Suppliers", "Account Heads",
  "Appointments", "Invoices", "Purchases", "Stock", "Expenses", "Income",
  "Journal", "Supplier Advances", "Supplier Payments", "Reports", "Users", "Audit Log",
];
const ACTIONS = ["View", "Create", "Edit", "Delete"];

const permKey = (mod, action) => `${mod.replace(/\s+/g, "")}:${action}`;

const RolesPermissions = () => {
  const dispatch = useDispatch();
  const { roles } = useSelector((s) => s.users);

  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [editRoleId, setEditRoleId] = useState(null);
  const [editRoleName, setEditRoleName] = useState("");

  useEffect(() => { dispatch(fetchRoles()); }, [dispatch]);

  const loadPermissions = async (role) => {
    setSelectedRole(role);
    setLoadingPerms(true);
    try {
      const res = await getRolePermissions(role.roleID ?? role.id);
      const perms = {};
      (res.data || []).forEach((p) => { perms[p] = true; });
      setPermissions(perms);
    } catch {
      setPermissions({});
    } finally {
      setLoadingPerms(false);
    }
  };

  const togglePerm = (mod, action) => {
    const key = permKey(mod, action);
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAllForModule = (mod) => {
    const allSet = ACTIONS.every((a) => permissions[permKey(mod, a)]);
    setPermissions((prev) => {
      const next = { ...prev };
      ACTIONS.forEach((a) => { next[permKey(mod, a)] = !allSet; });
      return next;
    });
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSavingPerms(true);
    try {
      const activePerms = Object.entries(permissions).filter(([, v]) => v).map(([k]) => k);
      await updateRolePermissions(selectedRole.roleID ?? selectedRole.id, activePerms);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Permissions saved", showConfirmButton: false, timer: 2000 });
    } catch {
      Swal.fire("Error", "Failed to save permissions.", "error");
    } finally {
      setSavingPerms(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      await dispatch(createRoleThunk({ roleName: newRoleName.trim() })).unwrap();
      setNewRoleName("");
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Role added", showConfirmButton: false, timer: 2000 });
    } catch {
      Swal.fire("Error", "Failed to create role.", "error");
    }
  };

  const handleUpdateRole = async (id) => {
    if (!editRoleName.trim()) return;
    try {
      await dispatch(updateRoleThunk({ id: id.roleID ?? id, data: { roleName: editRoleName.trim() } })).unwrap();
      setEditRoleId(null);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Role updated", showConfirmButton: false, timer: 2000 });
    } catch {
      Swal.fire("Error", "Failed to update role.", "error");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Roles & Permissions</h4><h6>Manage access control roles and their permissions</h6></div>
        </div>

        <div className="row">
          {/* Roles panel */}
          <div className="col-lg-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Roles</h6>
              </div>
              <div className="card-body p-0">
                <ul className="list-group list-group-flush">
                  {roles.map((role) => (
                    <li
                      key={role.id}
                      className={`list-group-item d-flex justify-content-between align-items-center ${selectedRole?.id === role.id ? "active" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      {editRoleId === role.id ? (
                        <div className="d-flex gap-1 flex-grow-1">
                          <input className="form-control form-control-sm" value={editRoleName}
                            onChange={(e) => setEditRoleName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleUpdateRole(role.id)} />
                          <button className="btn btn-sm btn-success px-2" onClick={() => handleUpdateRole(role.id)}>✓</button>
                          <button className="btn btn-sm btn-secondary px-2" onClick={() => setEditRoleId(null)}>✕</button>
                        </div>
                      ) : (
                        <>
                          <span className="flex-grow-1" onClick={() => loadPermissions(role)}>{role.roleName}</span>
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm p-1" title="Set Permissions" onClick={() => loadPermissions(role)}>
                              <Shield size={13} />
                            </button>
                            <button className="btn btn-sm p-1" title="Rename" onClick={() => { setEditRoleId(role.roleID ?? role.id); setEditRoleName(role.roleName); }}>
                              <Edit size={13} />
                            </button>
                            <button className="btn btn-sm p-1 text-danger" title="Delete"
                              onClick={() => confirmDelete(() => dispatch(deleteRoleThunk(role.roleID ?? role.id)), role.roleName)}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-footer">
                <div className="d-flex gap-2">
                  <input className="form-control form-control-sm" placeholder="New role name"
                    value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddRole()} />
                  <button className="btn btn-sm btn-primary px-3" onClick={handleAddRole} disabled={!newRoleName.trim()}>+</button>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions matrix */}
          <div className="col-lg-9">
            {!selectedRole ? (
              <div className="card text-center py-5 text-muted">
                <p>Select a role from the left panel to manage its permissions.</p>
              </div>
            ) : (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Permissions for: <strong>{selectedRole.roleName}</strong></h6>
                  <button className="btn btn-sm btn-submit" onClick={savePermissions} disabled={savingPerms || loadingPerms}>
                    {savingPerms ? "Saving..." : "Save Permissions"}
                  </button>
                </div>
                <div className="card-body p-0">
                  {loadingPerms ? (
                    <p className="p-3 text-muted">Loading permissions...</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ minWidth: 160 }}>Module</th>
                            {ACTIONS.map((a) => (
                              <th key={a} className="text-center" style={{ width: 90 }}>{a}</th>
                            ))}
                            <th className="text-center" style={{ width: 80 }}>All</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MODULES.map((mod) => {
                            const allSet = ACTIONS.every((a) => permissions[permKey(mod, a)]);
                            return (
                              <tr key={mod}>
                                <td className="fw-semibold">{mod}</td>
                                {ACTIONS.map((action) => (
                                  <td key={action} className="text-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={!!permissions[permKey(mod, action)]}
                                      onChange={() => togglePerm(mod, action)}
                                    />
                                  </td>
                                ))}
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allSet}
                                    onChange={() => toggleAllForModule(mod)}
                                    title="Toggle all"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
